import React from 'react';
import { TrendingUp, TrendingDown, Minus, Calendar, Target, Gauge, BarChart3 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { parseDateOnly } from '@/lib/dateOnly';
import type { MonteCarloResult } from '@/hooks/useEmpMonteCarlo';

interface Props {
  realized: number | null;
  planned: number | null;
  spi: number | null;
  mcLatest: MonteCarloResult | null;
  dataFimPrevista?: string | null;
}

const fmtDateBR = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = parseDateOnly(iso.slice(0, 10));
  return d ? format(d, 'dd/MMM/yy') : '—';
};

interface KPICardProps {
  icon: React.ElementType;
  label: string;
  value: string;
  subtext?: string;
  color: string;
  bgColor: string;
  tooltip: string;
  trend?: 'up' | 'down' | 'neutral';
  alert?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({
  icon: Icon, label, value, subtext, color, bgColor, tooltip, trend, alert,
}) => (
  <TooltipProvider delayDuration={200}>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={cn(
          'relative flex flex-col gap-1 rounded-lg border px-4 py-3 cursor-help transition-all duration-200',
          'hover:shadow-md hover:border-primary/20',
          alert && 'border-destructive/30 bg-destructive/5',
          !alert && 'border-border bg-card',
        )}>
          <div className="flex items-center justify-between">
            <div className={cn('flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider', color)}>
              <div className={cn('flex items-center justify-center w-5 h-5 rounded', bgColor)}>
                <Icon className="h-3 w-3" />
              </div>
              {label}
            </div>
            {trend && (
              <div className={cn(
                'flex items-center justify-center w-4 h-4 rounded-full',
                trend === 'up' && 'text-success',
                trend === 'down' && 'text-destructive',
                trend === 'neutral' && 'text-muted-foreground',
              )}>
                {trend === 'up' && <TrendingUp className="h-3 w-3" />}
                {trend === 'down' && <TrendingDown className="h-3 w-3" />}
                {trend === 'neutral' && <Minus className="h-3 w-3" />}
              </div>
            )}
          </div>
          <p className={cn('text-xl font-bold tracking-tight text-foreground leading-none')}>
            {value}
          </p>
          {subtext && (
            <p className="text-[10px] text-muted-foreground leading-tight">{subtext}</p>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs max-w-[240px]">
        <p>{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export const McExecutiveKPIs: React.FC<Props> = ({
  realized, planned, spi, mcLatest, dataFimPrevista,
}) => {
  // 1. Progress
  const deviation = realized != null && planned != null ? realized - planned : null;
  const progressAlert = deviation != null && deviation < -5;

  // 2. SPI — contextualizado pelo prazo
  const deadlinePassed = dataFimPrevista
    ? new Date(dataFimPrevista) < new Date()
    : false;

  const spiTrend = spi != null
    ? deadlinePassed ? (spi >= 1.0 ? 'neutral' : 'down')
    : spi >= 1.0 ? 'up' : spi >= 0.9 ? 'neutral' : 'down'
    : undefined;
  const spiAlert = spi != null && (spi < 0.9 || deadlinePassed);

  // 3. Previsão P50
  const p50DateStr = mcLatest?.p50_data ? fmtDateBR(mcLatest.p50_data) : '—';
  const p50VsPrazo = mcLatest?.p50_data && dataFimPrevista
    ? Math.round((new Date(mcLatest.p50_data).getTime() - new Date(dataFimPrevista).getTime()) / 86400000)
    : null;
  const p50Alert = p50VsPrazo != null && p50VsPrazo > 30;

  // 4. Probabilidade
  const prob = mcLatest?.prob_on_time;
  const probAlert = prob != null && prob < 50;

  const showSpiCard = !deadlinePassed && !(prob != null && prob === 0) && !(p50VsPrazo != null && p50VsPrazo > 30);

  return (
    <div className={cn('grid gap-3 mb-4', showSpiCard ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-3')}>
      <KPICard
        icon={BarChart3}
        label="Progresso"
        value={realized != null ? `${realized.toFixed(1)}%` : '—'}
        subtext={deviation != null
          ? `${deviation >= 0 ? '+' : ''}${deviation.toFixed(1)} p.p. vs planejado`
          : undefined}
        color="text-success"
        bgColor="bg-success/10"
        tooltip={`Progresso físico acumulado. Planejado: ${planned?.toFixed(1) ?? '—'}%. Desvio: ${deviation != null ? `${deviation >= 0 ? '+' : ''}${deviation.toFixed(1)} p.p.` : '—'}`}
        trend={deviation != null ? (deviation >= 0 ? 'up' : deviation > -3 ? 'neutral' : 'down') : undefined}
        alert={progressAlert}
      />

      {showSpiCard && (
        <KPICard
          icon={Gauge}
          label="Ritmo (SPI)"
          value={spi != null ? spi.toFixed(2) : '—'}
          subtext={spi != null
            ? spi >= 1.0 ? 'Dentro do ritmo planejado' : spi >= 0.9 ? 'Leve desvio de ritmo' : 'Abaixo do ritmo'
            : undefined}
          color="text-info"
          bgColor="bg-info/10"
          tooltip="SPI (Schedule Performance Index): velocidade atual vs planejada. ≥1.0 = no ritmo. 0.9–1.0 = atenção. <0.9 = risco de atraso."
          trend={spiTrend as any}
          alert={spiAlert}
        />
      )}

      <KPICard
        icon={Calendar}
        label="Previsão"
        value={p50DateStr}
        subtext={p50VsPrazo != null
          ? p50VsPrazo <= 0 ? `${Math.abs(p50VsPrazo)}d antes do prazo` : `${p50VsPrazo}d após o prazo`
          : undefined}
        color="text-chart-mc-median"
        bgColor="bg-[hsl(var(--chart-mc-median)/0.1)]"
        tooltip={`Data mais provável de conclusão (P50) via Monte Carlo${mcLatest?.n_iteracoes ? ` com ${mcLatest.n_iteracoes.toLocaleString()} iterações` : ''}.`}
        trend={p50VsPrazo != null ? (p50VsPrazo <= 0 ? 'up' : p50VsPrazo <= 30 ? 'neutral' : 'down') : undefined}
        alert={p50Alert}
      />

      <KPICard
        icon={Target}
        label="Prob. Prazo"
        value={prob != null ? `${prob.toFixed(0)}%` : '—'}
        subtext={prob != null
          ? prob >= 70 ? 'Provável no prazo' : prob >= 50 ? 'Risco moderado' : 'Alto risco'
          : undefined}
        color={prob != null && prob >= 70 ? 'text-success' : prob != null && prob >= 50 ? 'text-warning' : 'text-destructive'}
        bgColor={prob != null && prob >= 70 ? 'bg-success/10' : prob != null && prob >= 50 ? 'bg-warning/10' : 'bg-destructive/10'}
        tooltip={`Probabilidade de conclusão dentro do prazo contratual${dataFimPrevista ? ` (${fmtDateBR(dataFimPrevista)})` : ''}.`}
        trend={prob != null ? (prob >= 70 ? 'up' : prob >= 50 ? 'neutral' : 'down') : undefined}
        alert={probAlert}
      />
    </div>
  );
};
